import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HiArrowLeft, HiCheck } from "react-icons/hi";
import { PageLayout, ActionButton } from "../../../components/modern";
import { useAuth } from "../../../contexts/AuthContext";
import { adminApi } from "../../../utils/api";
import { getPasswordValidationErrorKey } from "../../../utils/passwordValidation";
import { showSuccessToast, showErrorToast } from "../../../utils/toastNotifications";
import "./EditUser.scss";

export const EditUser: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation(['users', 'common']);
  const { token } = useAuth();

  const [formData, setFormData] = useState({
    name: "",
    roleId: null,
    phone: "",
    password: "",
    email: "",
  });
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  // Fetch roles list
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await adminApi.call('/role', 'GET');
        if (!response.success) {
          showErrorToast(response.error || t('users:messages.fetchError'));
          return;
        }

        setRoles(response.data?.items || []);
      } catch (error) {
        showErrorToast(t('users:messages.fetchError'));
      }
    };

    fetchRoles();
  }, []);

  // Fetch user details
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await adminApi.call(`/user/${id}`, 'GET');

        if (!response.success) {
          showErrorToast(response.error || t('users:messages.fetchError'));
          return;
        }

        const data = response.data;
        setFormData({
          name: data.name,
          roleId: data.roleId,
          phone: data.phone,
          email: data.email,
          password:""
        });
      } catch (error: any) {
        showErrorToast(t('users:messages.fetchError') + ": " + error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [id]);

  // Handle input change
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (formData.password.trim()) {
      const passwordValidationErrorKey = getPasswordValidationErrorKey(formData.password, formData.phone);
      if (passwordValidationErrorKey) {
        newErrors.password = t(`users:validation.${passwordValidationErrorKey}`);
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const updateProfile = async () => {
    if (!validateForm()) {
      showErrorToast(t('users:validation.fillAllFields'));
      return;
    }

    setUpdating(true);

    try {
      const dataToSend = { ...formData, id };
      const response = await adminApi.call('/user/update', 'POST', dataToSend);

      if (!response.success) {
        showErrorToast(response.error || t('users:messages.updateError'));
        return;
      }

      showSuccessToast(t('users:messages.updateSuccess'));
    } catch (error: any) {
      showErrorToast(t('users:messages.updateError') + ": " + error.message);
    } finally {
      setUpdating(false);
    }
  };

  const breadcrumbs = [
    { label: t('users:breadcrumbs.users'), path: '/users' },
    { label: t('users:breadcrumbs.edit') }
  ];

  const headerActions = (
    <ActionButton
      variant="secondary"
      icon={HiArrowLeft}
      onClick={() => navigate('/users')}
    >
      {t('users:buttons.backToIndex')}
    </ActionButton>
  );

  if (loading) {
    return (
      <PageLayout
        title={t('users:editUser')}
        subtitle={t('users:subtitle')}
        breadcrumbs={breadcrumbs}
        headerActions={headerActions}
      >
        <div className="text-center mt-4">{t('common:loading')}</div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title={`${t('users:editUser')} - ${formData.name}`}
      subtitle={t('users:subtitle')}
      breadcrumbs={breadcrumbs}
      headerActions={headerActions}
    >
      <div className="modern-form">
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">{t('users:fields.name')}</label>
            <input
              type="text"
              className="form-control"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder={t('users:placeholders.name')}
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('users:fields.email')}</label>
            <input
              type="email"
              className="form-control"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder={t('users:placeholders.email')}
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('users:fields.phone')}</label>
            <input
              type="text"
              className="form-control"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder={t('users:placeholders.phone')}
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('users:fields.role')}</label>
            <select
              className="form-control"
              name="roleId"
              value={formData.roleId || ""}
              onChange={handleInputChange}
            >
              <option value="">{t('users:placeholders.role')}</option>
              {roles.map((item: any) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">{t('users:fields.password')}</label>
            <input
              type="password"
              className={`form-control ${errors.password ? 'is-invalid' : ''}`}
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder={t('users:placeholders.password')}
            />
            {errors.password && <div className="invalid-feedback">{errors.password}</div>}
          </div>
        </div>

        <div className="form-actions">
          <ActionButton
            variant="primary"
            icon={HiCheck}
            onClick={updateProfile}
            loading={updating}
            disabled={updating}
          >
            {updating ? t('users:buttons.updating') : t('users:buttons.update')}
          </ActionButton>
        </div>
      </div>
    </PageLayout>
  );
};
